import { ExternalLink } from 'lucide-react';
import type { FeedbackFormGift, FeedbackFormRank } from '@/types';
import { ConfettiEffect } from '../../Statuses/components/ConfettiEffect';
import { useFeedbackEdition } from './edition-context';

const SUPABASE_TSHIRT_IMG =
  'https://supabase.store/cdn/shop/products/tshirt_6fab2a57-963b-41bb-9ba5-0982128f2919_940x.png?v=1696844402';

interface FeedbackSubmittedProps {
  hackerName: string;
  supabaseGift: FeedbackFormGift | null;
  marketRank: FeedbackFormRank | null;
}

export function FeedbackSubmitted({
  hackerName,
  supabaseGift,
  marketRank,
}: FeedbackSubmittedProps) {
  const { homePath } = useFeedbackEdition();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 px-4 py-12">
      <ConfettiEffect />
      <div className="max-w-2xl text-center">
        <h1 className="mb-4 font-bold text-3xl text-primary sm:text-4xl">
          Gracias por tu feedback, {hackerName.split(' ')[0]}!
        </h1>
        <p className="mb-6 text-lg text-muted-foreground">
          Nos vemos en la próxima edición de Platanus Hack 👀
        </p>
      </div>

      <div className="w-full max-w-2xl space-y-6">
        {supabaseGift && (supabaseGift.url || supabaseGift.notes) && (
          <section className="relative overflow-hidden border-2 border-primary bg-background/80 p-6 backdrop-blur-sm">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-0"
            >
              <img
                src={SUPABASE_TSHIRT_IMG}
                alt=""
                className="absolute inset-0 h-full w-full object-contain object-right opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
            </div>
            <div className="relative z-10 sm:max-w-[70%]">
              <h2 className="font-bold text-primary text-xl sm:text-2xl">
                🎁 Regalo de Supabase
              </h2>
              <p className="mt-2 text-foreground text-sm sm:text-base">
                {marketRank
                  ? `Estuviste en el top ${marketRank.topPercent}% de traders de Platanus Hack Market (#${marketRank.rank} de ${marketRank.total}), te`
                  : 'Estuviste en el top de traders de Platanus Hack Market, te'}{' '}
                ganaste la CAMISA de{' '}
                <span className="font-bold text-primary">SUPABASE</span>!
              </p>
              <p className="mt-2 text-muted-foreground text-sm">
                Usa tu código en la Supabase Shop.
              </p>
              <p className="mt-2 text-muted-foreground text-xs italic sm:text-sm">
                Sólo válido para el ítem "T Shirt (Dark Mode)". Precio de lista
                $50.00. Aplicar descuento en checkout.
              </p>
              {supabaseGift.notes && (
                <div className="mt-4 border-primary border-l-4 pl-4">
                  <h3 className="font-bold text-primary/70 text-sm uppercase">
                    Tu código
                  </h3>
                  <p className="break-all font-mono text-lg text-primary sm:text-xl">
                    {supabaseGift.notes}
                  </p>
                </div>
              )}
              {supabaseGift.url && (
                <>
                  <div className="mt-4 border-primary border-l-4 pl-4">
                    <h3 className="font-bold text-primary/70 text-sm uppercase">
                      Tienda
                    </h3>
                    <p className="break-all font-mono text-primary text-sm sm:text-base">
                      {supabaseGift.url}
                    </p>
                  </div>
                  <a
                    href={supabaseGift.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 border-2 border-primary bg-primary px-5 py-2.5 font-bold text-background uppercase tracking-wide transition-all hover:bg-background hover:text-primary"
                  >
                    Canjear regalo
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </>
              )}
            </div>
          </section>
        )}
      </div>

      <a
        href={homePath}
        className="bg-primary px-6 py-3 font-bold text-background text-lg transition-all hover:scale-105"
      >
        Volver al inicio
      </a>
    </div>
  );
}
